import { INSIGHT } from './insight-gate/insight-rules.config';
import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  combineLatest,
  concat,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import type { Nullable } from '@ps/utils';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import type { SimilarSurveysQuery } from '../../api/similar-surveys/mappers/similar-surveys.payload.mapper';
import { SimilarSurveysService } from '../../services/similar-surveys/similar-surveys.service';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from '../audience/survey-creation-audience.actions';
import { selectSurveyFormState } from '../audience/survey-creation-audience.selectors';
import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from './survey-creation-insights.actions';
import {
  selectSimilarSurveysSearchedTargetingKey,
  selectSimilarSurveysSearchedGlobally,
  selectSimilarSurveysStale,
  selectSimilarSurveysStatus,
} from './survey-creation-insights.selectors';
import { InsightGate } from './insight-gate/insight-gate.service';
import {
  SIMILAR_SURVEYS_FAILURE_MESSAGE,
  SIMILAR_SURVEYS_SEARCH_COMPLETE_MESSAGE,
} from './utils/similar-surveys.constants';

interface SimilarSurveysSearchRequest {
  readonly searchGlobally: boolean;
  readonly notify: boolean;
}

@Injectable()
export class SurveyCreationSimilarSurveyInsightEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _insightGate = inject(InsightGate);
  private readonly _service = inject(SimilarSurveysService);
  private readonly _toast = inject(ToastService);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  private readonly _enabled$ = toObservable(
    computed(
      () => !!this._userSettingsSelectors.buyerSettings().enableSimilarSurveys,
    ),
  );

  public readonly searchSimilarSurveys$ = createEffect(() => {
    // Only the automatic branch is gated: the resync and outside-account
    // searches are explicit buyer requests from the CPI drawer.
    const automatic$ = this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
      ),
      this._insightGate.onlyIfAllowed(INSIGHT.SIMILAR_SURVEYS),
      map(
        (): SimilarSurveysSearchRequest => ({
          searchGlobally: false,
          notify: false,
        }),
      ),
    );

    const resync$ = this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.resyncSimilarSurveys),
      concatLatestFrom(() =>
        this._store.select(selectSimilarSurveysSearchedGlobally),
      ),
      map(
        ([, searchedGlobally]): SimilarSurveysSearchRequest => ({
          searchGlobally: searchedGlobally,
          notify: true,
        }),
      ),
    );

    const outsideAccount$ = this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.searchSimilarSurveysOutsideAccount),
      map(
        (): SimilarSurveysSearchRequest => ({
          searchGlobally: true,
          notify: true,
        }),
      ),
    );

    return merge(automatic$, resync$, outsideAccount$).pipe(
      switchMap(({ searchGlobally, notify }) =>
        this._query$().pipe(
          switchMap((query) =>
            concat(
              of(
                SurveyCreationInsightsApiActions.loadSimilarSurveysPending({
                  searchGlobally,
                }),
              ),
              this._search$(query, searchGlobally, notify),
            ),
          ),
        ),
      ),
    );
  });

  public readonly markSimilarSurveysStale$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
      ),
      concatLatestFrom(() => [
        this._store.select(selectSimilarSurveysStatus),
        this._store.select(selectSimilarSurveysSearchedTargetingKey),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSimilarSurveysStale),
      ]),
      filter(([, status]) => status !== 'loading'),
      filter(([, , , , isStale]) => !isStale),
      filter(([, , searchedTargetingKey, formState]) =>
        this._hasTargetingChanged(searchedTargetingKey, formState),
      ),
      map(() => SurveyCreationInsightsActions.markSimilarSurveysStale()),
    ),
  );

  public readonly notifySearchSucceeded$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationInsightsApiActions.loadSimilarSurveysSuccess),
        filter(({ notify }) => notify),
        tap(() => this._toast.success(SIMILAR_SURVEYS_SEARCH_COMPLETE_MESSAGE)),
      ),
    { dispatch: false },
  );

  private _search$(
    query: SimilarSurveysQuery,
    searchGlobally: boolean,
    notify: boolean,
  ): Observable<Action> {
    const searchedTargetingKey = this._service.targetingKey(query);

    return this._service.search(query, { searchGlobally }).pipe(
      concatLatestFrom(() => this._store.select(selectSurveyFormState)),
      map(([surveys, formState]) =>
        SurveyCreationInsightsApiActions.loadSimilarSurveysSuccess({
          surveys,
          searchGlobally,
          notify,
          targetingKey: searchedTargetingKey,
          isStale: this._hasTargetingChanged(searchedTargetingKey, formState),
        }),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationInsightsApiActions.loadSimilarSurveysFailure({
              error: error.message,
            }),
          ),
        { customMessage: SIMILAR_SURVEYS_FAILURE_MESSAGE, notify },
      ),
      takeUntil(
        this._actions$.pipe(
          ofType(SurveyCreationInsightsActions.cancelSimilarSurveysSearch),
        ),
      ),
    );
  }

  private _hasTargetingChanged(
    searchedTargetingKey: Nullable<string>,
    formState: ReturnType<typeof selectSurveyFormState>,
  ): boolean {
    if (!searchedTargetingKey) {
      return false;
    }
    return this._toQuery(formState)
      .map(
        (query) => this._service.targetingKey(query) !== searchedTargetingKey,
      )
      .unwrapOr(false);
  }

  private _query$(): Observable<SimilarSurveysQuery> {
    return combineLatest([
      this._store.select(selectSurveyFormState),
      this._enabled$,
    ]).pipe(
      filter(([, enabled]) => enabled),
      map(
        ([formState]): Option<SimilarSurveysQuery> => this._toQuery(formState),
      ),
      filter((query) => query.isSome()),
      take(1),
      map((query) => query.unwrap()),
    );
  }

  private _toQuery(
    formState: ReturnType<typeof selectSurveyFormState>,
  ): Option<SimilarSurveysQuery> {
    return Option.from(formState.metadata.value)
      .filter(
        (metadata) =>
          !!metadata.selectedCountries[0] && !!metadata.selectedLanguages[0],
      )
      .map((metadata) => ({
        metadata,
        qualifications: formState.qualifications.value,
        quotas: formState.quotas.value,
      }));
  }
}
